import { useTranslations } from "next-intl";
import { ReactNode, useCallback, useReducer } from "react";

import { Link } from "@zapier/design-system";

import { ZapHistoryExportFormat } from "@zapier/reporting/src/graphql-types";
import { useDownloadRuns } from "@zapier/reporting/src/hooks";
import { HistoryFiltersTypes } from "@zapier/reporting/src/types";
import {
  NotificationOptions,
  useNotification,
} from "../../global/Notifications";

export interface DownloadSelection {
  actionableRunCount: number;
  isAllSelected: boolean;
  runIds: string[];
}

export type ModalState =
  | { kind: "none" }
  | { kind: "delete"; onDelete: () => void }
  | { kind: "download"; selection: DownloadSelection }
  | {
      kind: "replay";
      allowTaskReplay: boolean;
      onReplay: () => void;
    }
  | { kind: "error"; error: string };

type ActiveModal = Exclude<ModalState, { kind: "none" }>;

type ModalAction =
  | { type: "CLOSE_MODAL" }
  | { type: "CLOSE_ERROR_MODAL" }
  | { modal: ActiveModal; type: "SET_MODAL" };

type ExportErrorTranslator = ReturnType<
  typeof useTranslations<"history-page.export-error-notification">
>;

function createExportSuccessNotification(
  t: ExportErrorTranslator
): NotificationOptions {
  return {
    message: t("success-message"),
    type: "success",
  };
}

function createExportErrorNotification(
  t: ExportErrorTranslator,
  message?: string
): NotificationOptions {
  return {
    message: (
      <>
        {t.rich("message", {
          link: (chunks: ReactNode) => (
            <Link href="/app/get-help">{chunks}</Link>
          ),
        })}
        {message && (
          <div>
            {t("error-label")} <em>{message}</em>
          </div>
        )}
      </>
    ),
    type: "error",
  };
}

function getErrorMessage(t: ExportErrorTranslator, error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === "string" && error.trim()) {
    return error;
  }

  return t("unknown-error");
}

function zapRunModalReducer(
  state: ModalState,
  action: ModalAction
): ModalState {
  switch (action.type) {
    case "SET_MODAL":
      return action.modal;
    case "CLOSE_MODAL":
      return { kind: "none" };
    case "CLOSE_ERROR_MODAL":
      if (state.kind !== "error") {
        return state;
      }

      return { kind: "none" };
    default:
      return state;
  }
}

export function useZapRunModals(filters: Partial<HistoryFiltersTypes>) {
  const t = useTranslations("history-page.export-error-notification");
  const { onDownload } = useDownloadRuns({ filters });
  const { createNotification } = useNotification();
  const [modal, dispatch] = useReducer(zapRunModalReducer, { kind: "none" });

  const closeModal = useCallback(() => {
    dispatch({ type: "CLOSE_MODAL" });
  }, []);

  const closeErrorModal = useCallback(() => {
    dispatch({ type: "CLOSE_ERROR_MODAL" });
  }, []);

  const openDeleteModal = useCallback((onDelete: () => void) => {
    dispatch({
      modal: { kind: "delete", onDelete },
      type: "SET_MODAL",
    });
  }, []);

  const openDownloadModal = useCallback(
    (actionableRunCount: number, isAllSelected: boolean, runIds: string[]) => {
      dispatch({
        modal: {
          kind: "download",
          selection: {
            actionableRunCount,
            isAllSelected,
            runIds,
          },
        },
        type: "SET_MODAL",
      });
    },
    []
  );

  const openReplayModal = useCallback(
    (onReplay: () => void, allowTaskReplay?: boolean) => {
      dispatch({
        modal: {
          allowTaskReplay: Boolean(allowTaskReplay),
          kind: "replay",
          onReplay,
        },
        type: "SET_MODAL",
      });
    },
    []
  );

  const showErrorModal = useCallback(
    (error: unknown) => {
      dispatch({
        modal: {
          error: getErrorMessage(t, error),
          kind: "error",
        },
        type: "SET_MODAL",
      });
    },
    [t]
  );

  const confirmDownloadRuns = useCallback(
    async (format: ZapHistoryExportFormat, excludeChildren: boolean) => {
      if (modal.kind !== "download") {
        return;
      }

      const { actionableRunCount, isAllSelected, runIds } = modal.selection;
      closeModal();

      try {
        await onDownload(
          runIds,
          isAllSelected,
          actionableRunCount,
          format,
          excludeChildren
        );
        createNotification(createExportSuccessNotification(t));
      } catch (error) {
        const message = error instanceof Error ? error.message : undefined;
        createNotification(createExportErrorNotification(t, message));
      }
    },
    [closeModal, createNotification, modal, onDownload, t]
  );

  return {
    modal,
    closeErrorModal,
    closeModal,
    confirmDownloadRuns,
    openDeleteModal,
    openDownloadModal,
    openReplayModal,
    showErrorModal,
  };
}
